import BaseApiService from './BaseApiService'
import type { ApprovalLevel, Folder, OverridesBelow, PromotionApprovalSource } from '../types'

export interface SaveFolderResponse {
  folderId: string
}

/** Payload of a 409 FOLDER_NOT_EMPTY from folders/delete. */
export interface FolderNotEmptyDetail {
  subfolderCount: number
  templateCount: number
  descendantFolderCount: number
  descendantTemplateCount: number
}

/** A folder-scoped API key a move or delete would affect (see folders/scope-impact). */
export interface AffectedApiKey {
  apiKeyId: string
  name: string
  keyPrefix: string
  isTest: boolean
  scopeFolderId: string
  scopeFolderName: string | null
}

export interface ScopeImpactResponse {
  affectedKeys: AffectedApiKey[]
  /** True for a delete: the keys are revoked rather than losing some templates. */
  revokes: boolean
}

/** A template whose effective promotion approval a move would change (folders/promotion-approval/move-impact). */
export interface ApprovalMoveChange {
  templateId: string
  name: string
  from: ApprovalLevel
  to: ApprovalLevel
}

export interface ApprovalMoveImpact {
  changes: ApprovalMoveChange[]
  count: number
}

export interface FolderDeleteResult {
  foldersDeleted: number
  templatesReassigned: number
  grantsRemoved: number
  reassignedToFolderId: string | null
}

export interface FolderPromotionApproval {
  setting: ApprovalLevel | null
  level: ApprovalLevel
  required: boolean
  source: Exclude<PromotionApprovalSource, 'template'>
  sourceFolderId: string | null
  overridesBelow: OverridesBelow
  /** False when the team has fewer than two active editors or admins; 'dual' is then refused. */
  dualAvailable: boolean
  dualUnavailableReason: string | null
}

class FoldersApiService extends BaseApiService {
  /** Create (omit folderId) or rename. Reparenting must go through `move`. */
  save(payload: {
    folderId?: string
    name: string
    parentFolderId?: string
  }): Promise<SaveFolderResponse> {
    return this.post<SaveFolderResponse>('/v1/folders/save', payload)
  }

  /** Direct children only. Use `listAll` to build the tree. */
  list(parentFolderId?: string): Promise<Folder[]> {
    return this.post<Folder[]>('/v1/folders/list', { parentFolderId })
  }

  /** Whole tree in one call, ordered by path so parents precede their children. */
  listAll(): Promise<Folder[]> {
    return this.post<Folder[]>('/v1/folders/list-all', {})
  }

  /** Reparents a folder. `targetParentFolderId` undefined/null moves it to the root. */
  move(folderId: string, targetParentFolderId?: string | null): Promise<{ folderId: string; parentFolderId: string | null }> {
    return this.post('/v1/folders/move', { folderId, targetParentFolderId: targetParentFolderId ?? null })
  }

  /** Fails with 409 FOLDER_NOT_EMPTY if the folder has subfolders or templates. */
  delete(folderId: string): Promise<void> {
    return this.post<void>('/v1/folders/delete', { folderId })
  }

  /**
   * Folder-scoped API keys a change would affect. Folders are not versioned, so a move or delete in
   * Test reaches Live at once. Pass templateIds (moving templates), folderId (moving a folder), or
   * folderId with del (deleting it). A null target is the root.
   */
  scopeImpact(change: { templateIds?: string[]; folderId?: string; targetFolderId?: string | null; del?: boolean }): Promise<ScopeImpactResponse> {
    return this.post<ScopeImpactResponse>('/v1/folders/scope-impact', {
      templateIds: change.templateIds,
      folderId: change.folderId,
      targetFolderId: change.targetFolderId ?? null,
      delete: change.del ?? false,
    })
  }

  /** Same body as scopeImpact (without del): templates whose effective approval level the move changes. */
  approvalMoveImpact(change: { templateIds?: string[]; folderId?: string; targetFolderId?: string | null }): Promise<ApprovalMoveImpact> {
    return this.post<ApprovalMoveImpact>('/v1/folders/promotion-approval/move-impact', {
      templateIds: change.templateIds,
      folderId: change.folderId,
      targetFolderId: change.targetFolderId ?? null,
    })
  }

  getPromotionApproval(folderId: string): Promise<FolderPromotionApproval> {
    return this.post<FolderPromotionApproval>('/v1/folders/promotion-approval/get', { folderId })
  }

  /** Team admins. applyToDescendants clears every override below so it follows this folder. */
  setPromotionApproval(folderId: string, level: ApprovalLevel | null, applyToDescendants: boolean): Promise<{ setting: ApprovalLevel | null; cleared: OverridesBelow }> {
    return this.post('/v1/folders/promotion-approval', { folderId, level, applyToDescendants })
  }

  /** Deletes the subtree. Contained templates move to the deleted folder's parent. */
  deleteRecursive(folderId: string): Promise<FolderDeleteResult> {
    return this.post<FolderDeleteResult>('/v1/folders/delete-recursive', { folderId })
  }
}

export const foldersApiService = new FoldersApiService()
